import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LANGUAGE_OPTIONS, type Language } from '@/admin/languages';
import {
  type EditableTaxonomy,
  parseTaxonomy,
  visibleKinds,
} from '@/admin/publishing/kinds';
import { TermsTable } from '@/admin/publishing/TermsTable';
import { Select } from '@/admin/ui/Form';
import { PageTitle } from '@/admin/ui/Page';
import { TabBar } from '@/admin/ui/TabBar';
import { useCurrentUser } from '~/hooks/useCurrentUser';

type TermsSearch = { taxonomy?: EditableTaxonomy; language?: Language };

const LANGUAGES = LANGUAGE_OPTIONS.map(([code]) => code);

export const Route = createFileRoute('/admin/post-terms')({
  staticData: { minTier: 'FRONT_END_SUPPORT' },
  validateSearch: (s: Record<string, unknown>): TermsSearch => ({
    taxonomy: parseTaxonomy(s.taxonomy),
    language: LANGUAGES.find((l) => l === s.language),
  }),
  component: PostTermsPage,
});

function PostTermsPage() {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const kinds = visibleKinds(me);
  // A taxonomy the tier may not edit would only 403; fall back to the first tab.
  const taxonomy =
    kinds.find((k) => k.taxonomy === search.taxonomy)?.taxonomy ??
    kinds[0]?.taxonomy;
  const language = search.language ?? 'EN';

  return (
    <div>
      <PageTitle>{t('admin.publishing.terms.title')}</PageTitle>
      <TabBar
        label={t('admin.publishing.terms.taxonomyTabs')}
        active={taxonomy}
        onSelect={(next) =>
          navigate({
            search: (prev) => ({ ...prev, taxonomy: next }),
            replace: true,
          })
        }
        tabs={kinds.map((k) => ({
          key: k.taxonomy,
          label: t(k.taxonomyLabelKey),
        }))}
      />
      <Bar>
        <Select
          aria-label={t('admin.common.language')}
          value={language}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            navigate({
              search: (prev) => ({
                ...prev,
                language: e.target.value as Language,
              }),
              replace: true,
            })
          }
        >
          {LANGUAGE_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {t(label)}
            </option>
          ))}
        </Select>
      </Bar>
      {taxonomy && (
        <TermsTable
          key={`${taxonomy}-${language}`}
          taxonomy={taxonomy}
          language={language}
        />
      )}
    </div>
  );
}

const Bar = styled.div`
  margin: var(--component-margin) 0 0;

  select {
    width: auto;
  }
`;
